import { useCallback, useRef, useState } from 'react';
import { useConfiguration } from '../context/configurationContext';
import CryptoJS from 'crypto-js';
import { getTranslatedRecipe } from './enums';
import moment from 'moment';
import { getUrlForLocale } from '../helpers/convert';

import articleImg from '../assets/symptom_filters/generic.png';
import nutritionPlansImage from '../assets/symptom_filters/plans.png';
import recipeImage from '../assets/symptom_filters/recipe.png';
import psychologyImage from '../assets/symptom_filters/psychology.png';
import fertilityImage from '../assets/symptom_filters/fertility.png';
import { break1, break2, break3 } from './constants';
import noImage from '../assets/no-image.jpg';
import { getSubscriptionData } from '../helpers/dataDupe';
import { hasValidSubscription } from './dataExtractors';

const SUPPORTED_LOCALES = ['en', 'de', 'el', 'ar'];
const LANGUAGE_SWITCH_SESSION_KEY = 'app.languageSwitch.inProgress';
const LANGUAGE_SWITCH_STARTED_AT_KEY = 'app.languageSwitch.startedAt';
const LANGUAGE_SWITCH_TARGET_LOCALE_KEY = 'app.languageSwitch.targetLocale';
const LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID = 'app-language-switch-overlay';
const LANGUAGE_SWITCH_BOOTSTRAP_STYLE_ID = 'app-language-switch-overlay-style';
const LANGUAGE_SWITCH_STALE_MS = 15000;
const LANGUAGE_SWITCH_SPINNER_DURATION = '3s';
const LANGUAGE_SWITCH_SPINNER_DELAY = '1.5s';
export const LANGUAGE_SWITCH_EVENT_NAME = 'app:language-switch:start';

const mountLanguageSwitchBootstrapStyle = () => {
  if (
    typeof document === 'undefined' ||
    document.getElementById(LANGUAGE_SWITCH_BOOTSTRAP_STYLE_ID)
  ) {
    return;
  }

  const styleEl = document.createElement('style');
  styleEl.id = LANGUAGE_SWITCH_BOOTSTRAP_STYLE_ID;
  styleEl.textContent = `
    #${LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID} {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 250, 246, 0.94);
      z-index: 2147483646;
      pointer-events: all;
    }

    #${LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID} .spinner {
      width: 48px;
      height: 48px;
      display: inline-block;
      position: relative;
    }

    #${LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID} .spinner::after,
    #${LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID} .spinner::before {
      content: '';
      box-sizing: border-box;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      border: 2px solid #ee8b51;
      position: absolute;
      left: 0;
      top: 0;
      animation: language-switch-ripple ${LANGUAGE_SWITCH_SPINNER_DURATION} linear infinite;
    }

    #${LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID} .spinner::after {
      animation-delay: ${LANGUAGE_SWITCH_SPINNER_DELAY};
    }

    @keyframes language-switch-ripple {
      0% {
        transform: scale(0);
        opacity: 1;
      }
      100% {
        transform: scale(1);
        opacity: 0;
      }
    }
  `;

  document.head.appendChild(styleEl);
};

export const mountLanguageSwitchBootstrapOverlay = () => {
  if (typeof document === 'undefined') return;

  mountLanguageSwitchBootstrapStyle();

  if (document.getElementById(LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID)) {
    return;
  }

  const overlay = document.createElement('div');
  overlay.id = LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');

  const spinner = document.createElement('div');
  spinner.className = 'spinner';
  overlay.appendChild(spinner);

  document.body.appendChild(overlay);
};

const dispatchLanguageSwitchStartEvent = locale => {
  if (typeof window === 'undefined') return;

  try {
    window.dispatchEvent(new CustomEvent(LANGUAGE_SWITCH_EVENT_NAME, { detail: { locale } }));
  } catch (err) {
    // Ignore browser-specific CustomEvent constructor issues.
  }
};

export const markLanguageSwitchInProgress = locale => {
  if (typeof window === 'undefined') return;

  sessionStorage.setItem(LANGUAGE_SWITCH_SESSION_KEY, 'true');
  sessionStorage.setItem(LANGUAGE_SWITCH_STARTED_AT_KEY, String(Date.now()));
  if (locale) {
    sessionStorage.setItem(LANGUAGE_SWITCH_TARGET_LOCALE_KEY, locale);
  }

  mountLanguageSwitchBootstrapOverlay();
  dispatchLanguageSwitchStartEvent(locale);
};

export const clearLanguageSwitchInProgress = () => {
  if (typeof window === 'undefined') return;

  sessionStorage.removeItem(LANGUAGE_SWITCH_SESSION_KEY);
  sessionStorage.removeItem(LANGUAGE_SWITCH_STARTED_AT_KEY);
  sessionStorage.removeItem(LANGUAGE_SWITCH_TARGET_LOCALE_KEY);

  if (typeof document !== 'undefined') {
    const overlay = document.getElementById(LANGUAGE_SWITCH_BOOTSTRAP_OVERLAY_ID);
    if (overlay) {
      overlay.remove();
    }
  }
};

export const isLanguageSwitchInProgress = () => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(LANGUAGE_SWITCH_SESSION_KEY) === 'true';
};

export const getLanguageSwitchStartedAt = () => {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(LANGUAGE_SWITCH_STARTED_AT_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const switchAppLanguage = ({ locale, pathname, search, hash, replace = false } = {}) => {
  if (typeof window === 'undefined' || !locale) return false;

  const currentLocale = getDefaultLocale();
  if (currentLocale === locale) return false;

  markLanguageSwitchInProgress(locale);
  window.localStorage.setItem('locale', locale);

  const basePath = pathname || window.location.pathname;
  const nextPath = getUrlForLocale(basePath, locale);
  const nextSearch = search != null ? search : window.location.search || '';
  const nextHash = hash != null ? hash : window.location.hash || '';
  const nextUrl = `${nextPath}${nextSearch}${nextHash}`;

  window.requestAnimationFrame(() => {
    if (replace) {
      window.location.replace(nextUrl);
    } else {
      window.location.assign(nextUrl);
    }
  });

  return true;
};

export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr?.length > 0 ?? false);
};

export const isLocaleGreek = () => {
  const config = useConfiguration();
  const locale = config.localization.locale;
  return locale === 'el';
};

export const isLocaleEnglish = () => {
  const config = useConfiguration();
  const locale = config.localization.locale;
  return locale === 'en';
};

export const isLocaleGerman = () => {
  const config = useConfiguration();
  const locale = config.localization.locale;
  return locale === 'de';
};

export const onSendEmail = (body = null) => {
  const isValidBody = typeof body === 'string';

  if (typeof window !== 'undefined') {
    const email = 'info@vivianlab.com';
    const encodedBody = isValidBody && encodeURIComponent(body);
    const messageWithBody = isValidBody
      ? `mailto:${email}?subject=Contact VivianLab&body=${encodedBody}`
      : `mailto:${email}`;
    window.location.href = messageWithBody;
  }
};

// Encryption function
export const encrypt = (plaintext, secretKey) => {
  const ciphertext = CryptoJS.AES.encrypt(plaintext, secretKey).toString();
  return ciphertext;
};

// Decryption function
export const decrypt = (ciphertext, secretKey) => {
  const bytes = CryptoJS.AES.decrypt(ciphertext, secretKey);
  const plaintext = bytes.toString(CryptoJS.enc.Utf8);
  return plaintext;
};

export function isFunction(value) {
  return typeof value === 'function';
}

export const useStateRef = initialState => {
  const [state, setState] = useState(initialState);
  const ref = useRef(initialState);

  const dispatch = useCallback(stateToSet => {
    ref.current = isFunction(stateToSet) ? stateToSet(ref.current) : stateToSet;
    setState(ref.current);
  }, []);

  return [state, dispatch, ref];
};

export const isImageLink = link => {
  // Supported image file extensions
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic', 'heif'];

  // Get the file extension from the link
  const extension = link
    .split('.')
    .pop()
    .toLowerCase();

  // Check if the extension matches any of the image extensions
  return imageExtensions.includes(extension);
};

export const appendAverageReviews = reviews => {
  const MIN_RATING = 1;
  const MAX_RATING = 5;

  const filteredReviews =
    Array.isArray(reviews) &&
    reviews.filter(r => r?.attributes?.rating !== null && r?.attributes?.state === 'public');

  const totalReviews = filteredReviews?.length || 0;

  if (!totalReviews) {
    return null;
  }

  const ratings = filteredReviews.map(review => review?.attributes?.rating);
  const validRatings = ratings.filter(r => r >= MIN_RATING && r <= MAX_RATING);

  if (!validRatings.length) {
    return null;
  }

  const starSum = validRatings.reduce((partialSum, rating) => partialSum + rating, 0);
  const averageRating = starSum / validRatings.length;

  return averageRating && averageRating.toFixed(1);
};

export const getPreferredLanguage = user => {
  return user?.attributes?.profile?.protectedData?.preferredLang || 'gr';
};

export const switchToUserPreferredLanguage = (user, defer = false) => {
  if (typeof window === 'undefined') return;

  const userPreferredLang = user?.attributes?.profile?.protectedData?.preferredLang;

  if (!userPreferredLang) {
    return;
  }

  const currentLocale = getDefaultLocale();

  const preferredLangToLocale = {
    gr: 'el', // Greek
    en: 'en', // English
    de: 'de', // German
  };

  const targetLocale = preferredLangToLocale[userPreferredLang];

  if (targetLocale && targetLocale !== currentLocale) {
    const hasSwitchedLanguage = sessionStorage.getItem('hasSwitchedLanguage');

    if (!hasSwitchedLanguage) {
      sessionStorage.setItem('hasSwitchedLanguage', 'true');

      if (defer) {
        markLanguageSwitchInProgress(targetLocale);
        window.localStorage.setItem('locale', targetLocale);
        sessionStorage.setItem('pendingLanguageSwitch', 'true');
      } else {
        switchAppLanguage({
          locale: targetLocale,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          replace: true,
        });
      }
    }
  }
};

export const isRtlLocale = locale => locale === 'ar';

const setDocumentLang = locale => {
  if (typeof document !== 'undefined') {
    const rtl = isRtlLocale(locale);
    document.documentElement.setAttribute('lang', locale);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');

    if (document.body) {
      document.body.setAttribute('dir', rtl ? 'rtl' : 'ltr');
      document.body.classList.toggle('rtl-text', rtl);
    }
  }
};

export const handleDeferredLanguageSwitch = () => {
  if (typeof window === 'undefined') return;

  // Set lang + moment locale on every boot
  const locale = getDefaultLocale();
  setDocumentLang(locale);

  if (window.moment) {
    window.moment.locale(locale);
  }

  const startedAt = getLanguageSwitchStartedAt();
  if (isLanguageSwitchInProgress()) {
    if (startedAt && Date.now() - startedAt > LANGUAGE_SWITCH_STALE_MS) {
      clearLanguageSwitchInProgress();
    } else {
      mountLanguageSwitchBootstrapOverlay();
    }
  }

  // keep your existing pending reload behavior (optional)
  const pendingSwitch = sessionStorage.getItem('pendingLanguageSwitch');
  if (pendingSwitch === 'true') {
    sessionStorage.removeItem('pendingLanguageSwitch');
    markLanguageSwitchInProgress(locale);
    window.location.reload();
  }
};

export const getDefaultLocale = () => {
  if (typeof window === 'undefined') return 'en'; // SSR default

  // Read locale from URL first: /el, /de (English is default at root without /en)
  const pathLocale = window.location.pathname.split('/')[1];
  if (SUPPORTED_LOCALES.includes(pathLocale)) {
    return pathLocale; // 'el' | 'de' | 'en' (if you ever keep /en)
  }

  // Then storage
  const stored = window.localStorage.getItem('locale');
  if (SUPPORTED_LOCALES.includes(stored)) return stored;

  // Fallback: English
  return 'en';
};

/**
 * Check if a pathname is a root path (including locale prefixes)
 * Root paths are: '/', '/el', '/de', '/ar', '/en', '/en/'
 */
export const isRootPath = pathname => {
  if (!pathname) return false;
  const normalizedPath = pathname.replace(/\/$/, '');
  return (
    normalizedPath === '/' ||
    normalizedPath === '' ||
    SUPPORTED_LOCALES.some(locale => normalizedPath === `/${locale}`)
  );
};

export const sortArrayAlphabetically = array => {
  // Clone the original array to avoid modifying the original data
  const sortedArray = [...array];

  // Sort the array alphabetically by the "value" property
  sortedArray.sort((a, b) => a.label.localeCompare(b.label));

  return sortedArray;
};

export const sortGreekLabels = data => {
  return data.sort((a, b) => new Intl.Collator('el').compare(a.label, b.label));
};

export const sortGreekCountriesLabels = data => {
  return data.sort((a, b) => new Intl.Collator('el').compare(a.name, b.name));
};

export const removeOfflineOption = services => {
  // Map through each service in the array
  return services.map(service => {
    // Filter out the 'offline' option from the bookingOptions array
    const updatedBookingOptions = service.bookingOptions.filter(option => option !== 'Offline');

    // Return the modified service object with the updated bookingOptions
    return {
      ...service,
      bookingOptions: updatedBookingOptions,
    };
  });
};

export const calculateSubscriptionPrice = ({ price: priceStr, percentageOff, currency }) => {
  // Extract the numerical part of the price from the string
  const price = !!priceStr && parseFloat(priceStr.replace(/[^\d.-]/g, ''));

  // Calculate the discount amount
  const discount = (price * percentageOff) / 100;

  // Calculate the final price
  const finalPrice = price - discount;

  // Format the price with two decimal places for better precision
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(finalPrice);

  return { finalPrice: formattedPrice, priceAmount: parseFloat(finalPrice.toFixed(2)) };
};

export const sanitizeString = value => {
  if (typeof value === 'string') {
    return value.replace('[object Object]', '').trim();
  }
  return value;
};

const shuffleImages = imagePaths => {
  let shuffled = imagePaths;
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const getUniqueImages = (index, imagePaths) => {
  let images = shuffleImages(imagePaths);

  // Use the index directly for more custom logic
  // For example: Ensure no image repeats more than 4 times
  if (index % 4 === 0 && index !== 0) {
    // Shuffle again after every 4 images
    images = shuffleImages(imagePaths);
  }

  // Return the image based on index
  return images[index % images.length];
};

/**
 * Simple hash function for string to number conversion
 * Returns consistent hash for same input string
 * @param {string} str - String to hash
 * @returns {number} - Hash value
 */
const simpleHash = str => {
  let hash = 0;
  if (!str || str.length === 0) return hash;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Get deterministic thumbnail for written guide
 * - If guide has Strapi thumbnail, return it
 * - Otherwise, return one of 4 default thumbnails based on guide ID hash
 * This ensures the same guide always shows the same thumbnail across all pages
 *
 * @param {Object} guide - Written guide object
 * @param {Array} defaultThumbnails - Array of default thumbnail paths (optional)
 * @returns {string} - Thumbnail URL or path
 */
export const getWrittenGuideThumbnail = (guide, defaultThumbnails = null) => {
  // First check if Strapi has a thumbnail
  const strapiThumbnail = guide?.attributes?.thumbnail?.data?.attributes?.url;
  if (strapiThumbnail) {
    return strapiThumbnail;
  }

  const thumbnails = defaultThumbnails || [
    require('../assets/thumbnail1.png'),
    require('../assets/thumbnail2.png'),
    require('../assets/thumbnail3.png'),
    require('../assets/thumbnail4.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail1.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail2.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail3.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail4.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail5.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail6.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail7.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail8.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail9.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail10.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail11.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail12.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail13.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail14.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail15.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail16.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail17.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail18.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail19.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail20.png'),
    require('../containers/TopicDetailsPage/icons/thumbnail21.png'),
  ];

  const guideId = (guide?.id || guide?.attributes?.slug || '').toString();
  const hash = simpleHash(guideId);
  const index = hash % thumbnails.length;

  return thumbnails[index];
};

export const parseUrlToOriginal = search => {
  // Replace hyphens with spaces and capitalize each word
  return search
    ?.split('-')
    ?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))
    ?.join(' ');
};

export const getLastUpdatedTime = items => {
  if (!items.length) return 'No updates available';

  // Filter out items with invalid or missing `updatedAt` dates
  const validItems = items.filter(
    item => item?.attributes?.updatedAt && !isNaN(new Date(item?.attributes?.updatedAt))
  );

  if (!validItems.length) return 'No valid updates available';

  // Find the most recent `updatedAt` timestamp
  const mostRecent = validItems.reduce((latest, item) => {
    return new Date(item?.attributes?.updatedAt) > new Date(latest?.attributes?.updatedAt)
      ? item
      : latest;
  });

  // Calculate the time difference
  const currentTime = new Date();
  const updatedAtTime = new Date(mostRecent?.attributes?.updatedAt);
  const diffMs = currentTime - updatedAtTime;

  // Convert milliseconds to hours and minutes
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  // Return formatted result
  if (diffHours > 0) {
    return `Updated ${diffHours} hour(s) ago`;
  } else {
    return `Updated ${diffMinutes} minute(s) ago`;
  }
};

/**
 * Function to filter events based on voucher existence and user eligibility
 * @param {Array} events - List of events
 * @param {string} ownVoucher - User's voucher
 * @returns {Array} - Filtered list of events
 */
export const createEventFilter = (events, ownVoucher) => {
  const normalizedOwnVoucher = ownVoucher?.toLowerCase() || '';

  return events.filter(event => {
    // pull out the coupon name (if any) in lowercase
    const eventVoucherName =
      event?.attributes?.voucher?.data?.attributes?.company?.toLowerCase() || '';

    // 1. No coupon on this event → visible to all
    if (!eventVoucherName) {
      return true;
    }

    // 2. Coupon present → only show if it matches the user's ownVoucher
    return eventVoucherName === normalizedOwnVoucher;
  });
};

export const filterEventsWithoutCompanyVoucher = events => {
  return events?.filter(e => !e?.attributes?.voucher?.data?.attributes?.company);
};

export const getStrapiLocale = () => {
  const defaultLocale = getDefaultLocale();
  return defaultLocale === 'el' ? 'el-GR' : defaultLocale;
};

export const getNeutralizedString = str => {
  if (!str) return '';
  const locale = getStrapiLocale();
  if (str.endsWith(locale)) return str.slice(0, str.length - locale.length - 1);
  return str;
};

// Function to get options by language
export const getOptionsByLanguage = (locale, uniqueTypes) => {
  return uniqueTypes.map(type => getTranslatedRecipe(type, locale));
};

export const getQuestionsByLang = lang => {
  const greekQuestions = [
    {
      id: 'AIChatPage.defaultQuestion1',
      message: 'τι είναι η θεραπεία ορμονικής υποκατάστασης;',
    },
    {
      id: 'AIChatPage.defaultQuestion2',
      message: 'ποιες προτάσεις δίνονται για την παρακολούθηση των συμπτωμάτων;',
    },
    {
      id: 'AIChatPage.defaultQuestion3',
      message: 'ποιες ομάδες τροφίμων είναι σημαντικές για την ενίσχυση της σεξουαλικής διάθεσης;',
    },
    {
      id: 'AIChatPage.defaultQuestion4',
      message: 'αν πιω αλκοόλ θα μπορέσω να κοιμηθώ πιο εύκολα;',
    },
  ];

  const englishQuestions = [
    {
      id: 'AIChatPage.defaultQuestion1',
      message: 'What is Hormone Replacement Therapy?',
    },
    {
      id: 'AIChatPage.defaultQuestion2',
      message: 'What causes mood swings in menopause?',
    },
    {
      id: 'AIChatPage.defaultQuestion3',
      message: 'What causes hot flashes?',
    },
    {
      id: 'AIChatPage.defaultQuestion4',
      message: 'Will I gain weight if I don’t sleep well?',
    },
  ];

  return lang === 'el' ? greekQuestions : englishQuestions;
};

export const sortRecurringEventsMaybe = events => {
  const now = new Date().toISOString();

  return events.sort((a, b) => {
    const getNextValidDate = event => {
      if (event.recurringDates?.length) {
        // Get future recurring dates
        const futureDates = event.recurringDates
          .map(d => new Date(d.Date))
          .filter(d => d >= new Date(now))
          .sort((d1, d2) => d1 - d2);
        return futureDates.length ? futureDates[0] : new Date(event.Date); // Use main date if no future recurring
      }
      return new Date(event.Date); // No recurring dates, use main event date
    };

    const dateA = getNextValidDate(a);
    const dateB = getNextValidDate(b);

    return dateA - dateB;
  });
};

// Helper function to filter events into upcoming and past

export const filterEventsByDate = events => {
  const now = moment();

  const upcomingEvents = [];
  const pastEvents = [];

  events?.forEach(event => {
    const { attributes } = event;
    const eventDuration = attributes?.duration || 0;

    // Helper function to check if a date is in the future after adding duration
    const isDateUpcoming = date =>
      moment(date)
        .add(eventDuration, 'minutes')
        .isSameOrAfter(now);

    // Check if event has any upcoming dates (regular or recurring)
    const isUpcoming = isArrayLength(attributes?.recurringDates)
      ? attributes.recurringDates.some(rd => isDateUpcoming(rd.Date))
      : isDateUpcoming(attributes?.date);

    // Add to appropriate array
    if (isUpcoming) {
      upcomingEvents.push(event);
    } else {
      pastEvents.push(event);
    }
  });

  return { upcomingEvents, pastEvents };
};

// Helper function to sort events
export const sortEvents = events => {
  return events?.sort((a, b) => {
    const now = moment(); // Current date and time
    const aDate = moment(a?.attributes?.date); // Parse event date
    const bDate = moment(b?.attributes?.date);

    // If both events are in the future, sort by proximity to now
    if (aDate.isSameOrAfter(now) && bDate.isSameOrAfter(now)) {
      return aDate - bDate; // Closest event to now goes first
    }

    // If one is in the future and the other is in the past, past event goes first
    if (aDate.isSameOrAfter(now) && bDate.isBefore(now)) {
      return 1; // a is future, b is past -> b goes before a
    } else if (aDate.isBefore(now) && bDate.isSameOrAfter(now)) {
      return -1; // a is past, b is future -> a goes before b
    }

    // If both events are in the past, maintain order or sort by date if needed
    return aDate - bDate;
  });
};

export const sortWebinarEventsByDate = events => {
  if (!Array.isArray(events)) return [];

  const currentTime = Date.now();
  const seenIds = new Set();
  const flattenedEvents = [];

  events.forEach(event => {
    if (!event || !event.id || !event.attributes || !event.attributes.date) return;

    const { id, attributes } = event;
    const { date, recurringDates } = attributes;

    // Check and push base date if valid and not already included
    const baseTime = new Date(date).getTime();
    if (baseTime >= currentTime && !seenIds.has(id)) {
      flattenedEvents.push({
        ...event,
        occurrenceDate: baseTime,
      });
      seenIds.add(id);
      return; // Skip adding recurringDates if base date was valid and added
    }

    // Fallback to recurring dates if base date is past
    if (Array.isArray(recurringDates) && !seenIds.has(id)) {
      for (const rd of recurringDates) {
        if (rd?.Date) {
          const recurringTime = new Date(rd.Date).getTime();
          if (recurringTime >= currentTime) {
            flattenedEvents.push({
              ...event,
              occurrenceDate: recurringTime,
            });
            seenIds.add(id);
            break; // Only one entry per ID
          }
        }
      }
    }
  });

  return flattenedEvents.sort((a, b) => a.occurrenceDate - b.occurrenceDate).slice(0, 3);
};

export const shouldEnableProducts = () => {
  return process.env.REACT_APP_ENABLE_PRODUCTS === 'true';
};

export const filterGuidesByType = (writtenGuides, types) => {
  const typeArray = Array.isArray(types) ? types : [types];

  return writtenGuides.filter(guide => {
    const guideCategories = guide?.attributes?.categories || [];
    return typeArray.every(type => guideCategories.includes(type));
  });
};

export const generateRandomUserData = () => {
  const randomString = Math.random()
    .toString(36)
    .substring(2, 10);
  const firstName = `User${randomString}`;
  const lastName = `Smith${randomString}`;
  return { firstName, lastName };
};

export const isRestrictedToOneTopic = tag => {
  const restrictedTags = ['Διαχείριση Άγχους & Στρες', 'Anxiety & Stress Management'];

  return restrictedTags.includes(tag);
};

export const getFavorites = currentUser =>
  currentUser?.attributes?.profile?.publicData?.favorites || [];

export const getWatchedVideos = currentUser =>
  currentUser?.attributes?.profile?.publicData?.watchedVideos || [];

export const getWatchedWrittenGuides = currentUser =>
  currentUser?.attributes?.profile?.publicData?.watchedWrittenGuides || [];

export const getStreamingVideos = currentUser =>
  currentUser?.attributes?.profile?.publicData?.watchedVideos || [];

export const getTitleFromSlug = slug => {
  if (!slug) return '';
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

export const extractImageUrl = description => {
  const imageUrlMatch = description.match(/!\[.*?\]\((.*?)\)/);
  return imageUrlMatch ? imageUrlMatch[1] : null;
};

export const formatTime = seconds => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
};

export const seekToChapter = (time, playerRef) => {
  if (playerRef.current) {
    playerRef.current
      .setCurrentTime(time)
      .then(() => {
        playerRef.current.play();
      })
      .catch(error => {
        console.error('VimeoPlayer: Error seeking to chapter:', error);
      });
  }
};
export const formatRemainingTime = time => {
  const locale = getDefaultLocale();
  const translations = {
    en: {
      sec: 'sec',
      min: 'min',
      remaining: 'remaining',
    },
    de: {
      sec: 'Sek.',
      min: 'Min.',
      remaining: 'verbleibend',
    },
    el: {
      sec: 'δευτ.',
      min: 'λεπ.',
      remaining: 'απομένουν',
    },
  };

  const t = translations[locale] || translations.en;

  if (!time || time < 0) return `0 ${t.sec} ${t.remaining}`;
  if (time < 60) {
    return `${Math.floor(time)} ${t.sec} ${t.remaining}`;
  }

  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes} ${t.min} ${seconds} ${t.sec} ${t.remaining}`;
};

export function getImageByType(typeImageMap, type) {
  return typeImageMap[type] || null; // fallback to null or a default image
}

export function cleanVimeoUrl(url = '') {
  if (!url) return '';

  try {
    // Vimeo links should be of format:
    // https://player.vimeo.com/video/{id}?h=hash
    const base = url.split('?')[0]; // keep main video part
    const params = new URL(url).searchParams;

    // Only keep the "h" param if it exists
    const h = params.get('h');
    return h ? `${base}?h=${h}` : base;
  } catch (e) {
    // fallback if URL parsing fails
    return url;
  }
}

export const ParentLabel = {
  NUTRITION: 'Nutrition',
  RECIPE: 'Recipe',
  ARTICLES: 'Articles',
  RESOURCES: 'Resources',
};

const parentLabelTranslations = {
  en: {
    [ParentLabel.NUTRITION]: 'Nutrition',
    [ParentLabel.RECIPE]: 'Recipe',
    [ParentLabel.RESOURCES]: 'Resources',
    [ParentLabel.ARTICLES]: 'Articles',
  },
  el: {
    [ParentLabel.NUTRITION]: 'Διατροφή',
    [ParentLabel.RECIPE]: 'Συνταγή',
    [ParentLabel.RESOURCES]: 'Πόροι',
    [ParentLabel.ARTICLES]: 'Άρθρα',
  },
  de: {
    [ParentLabel.NUTRITION]: 'Ernährung',
    [ParentLabel.RECIPE]: 'Rezept',
    [ParentLabel.RESOURCES]: 'Ressourcen',
    [ParentLabel.ARTICLES]: 'Artikel',
  },
};

export function getLocalizedArticleParentLabel(label, locale) {
  if (!parentLabelTranslations[locale]) {
    locale = 'el'; // fallback to English
  }
  return parentLabelTranslations[locale][label] || label;
}

export const typeImageMap = {
  menopause: articleImg,
  nutrition: nutritionPlansImage,
  recipe: recipeImage,
  psychology: psychologyImage,
  fertility: fertilityImage,
};

export const markdownHasImage = s =>
  !!s && (/[!]\[[^\]]*]\([^)"']+\)/i.test(s) || /<img\b[^>]*\bsrc=["'][^"']+["']/i.test(s));

/**
 * Remove a trailing locale suffix like -el, -el-GR, -de-DE, -pt-BR, etc.
 * Examples:
 *  "nutrition-el-GR" -> "nutrition"
 *  "psychology-el"   -> "psychology"
 */
const stripTrailingLocale = slug =>
  typeof slug === 'string' ? slug.replace(/-(?:[a-z]{2}(?:-[A-Z]{2})?)$/, '') : slug;

/**
 * Resolve a hero image for a given topic slug that may include a locale suffix.
 */
export const chooseHeroImage = (attributes, map = typeImageMap, fallback = articleImg) => {
  if (markdownHasImage(attributes?.description)) {
    return { hasImageInContent: true, hero: null };
  }

  const slug = attributes?.topic?.data?.attributes?.slug || '';
  let hero = null;

  // 1) Exact match
  if (map[slug]) {
    hero = map[slug];
  } else {
    // 2) Try base (strip trailing locale)
    const base = stripTrailingLocale(slug);
    if (map[base]) {
      hero = map[base];
    } else {
      // 3) Fallback heuristics: find any key that appears as a prefix or segment
      const key = Object.keys(map).find(
        k =>
          slug === k ||
          slug.startsWith(`${k}-`) ||
          slug.includes(`-${k}-`) || // rare, but keeps it resilient
          slug.endsWith(`-${k}`)
      );
      hero = key ? map[key] : null;
    }
  }

  return { hasImageInContent: false, hero: hero || fallback };
};

export const isBioiatriki = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.location.pathname.includes('bioiatriki');
  } catch {
    return false;
  }
};

export const capitalizeWords = (str = '') => {
  return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

export const getQuickWinGuides = (writtenGuides, videoGuides) => {
  const normalize = (guides, type) => {
    if (!Array.isArray(guides)) return [];
    return guides
      .filter(g => g?.attributes?.isQuickWin === true)
      .map(g => ({
        ...g,
        type, // add "written" or "video"
      }));
  };

  const written = normalize(writtenGuides, 'written');
  const video = normalize(videoGuides, 'video');

  return [...written, ...video];
};

/**
 * Greek language keyboard layout normalization helper
 *
 * In Greek keyboards in order to achieve representation
 * of some diacritic characters
 * and also some punctuation marks
 * the utf-8 characters are used mid-words or in combinations
 *
 * we are going to make this heleper method as detailed as possible
 * and with few important goals:
 * 1. pre-scan everything to detect what's what needs flattening,
 * 2. this way we will skip costly ops (e.g. NFD) if they are not needed
 * 3. resource save, since it will be used for global search
 * 4. set also timers so performance can be monitored on case to case bases
 */
export function normalizeGreek(input = '', opts = { stats: false }) {
  // START TEST PATTERNS
  const TONOS = /\u0384/g; // ΄ (tonos)
  const GREEK_QMARK = /\u037E/g; // ;
  const ANO_TELEIA = /\u0387/g; // ·
  const FINAL_SIGMA = /\u03C2/g; // ς
  const ASCII_SEMI = /;/g; // ;
  const COMBINING_MARKS = /\p{M}+/gu; // any combining marks
  const HAS_UPPER = /[A-Z\u0391-\u03A9]/; // Uppercase (Latin + Greek)
  const HAS_EXTRA_SPACES = /\s{2,}/; // Extra spaces
  const NEEDS_NFD = /[\p{M}\u0384-\u038A\u038C\u038E-\u03CE\u1F00-\u1FFF]/u; // diacritic test
  // END TEST PATTERNS

  // Stopwatch set
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();

  let out = input;
  let worked = false;

  // Check and apply switch if any diacritic characters occurences
  if (NEEDS_NFD.test(out)) {
    out = out.normalize('NFD').replace(COMBINING_MARKS, '');
    worked = true;
  }

  // Check and apply switch if any punctiation occurences
  if (ASCII_SEMI.test(out)) {
    out = out.replace(ASCII_SEMI, '');
    worked = true;
  }
  if (TONOS.test(out)) {
    out = out.replace(TONOS, '');
    worked = true;
  }
  if (GREEK_QMARK.test(out)) {
    out = out.replace(GREEK_QMARK, '');
    worked = true;
  }
  if (ANO_TELEIA.test(out)) {
    out = out.replace(ANO_TELEIA, '');
    worked = true;
  }
  if (FINAL_SIGMA.test(out)) {
    out = out.replace(FINAL_SIGMA, '\u03C3');
    worked = true;
  }

  // Check and apply lowercasing & spaces if needed
  if (HAS_UPPER.test(out)) {
    out = out.toLowerCase();
    worked = true;
  }
  if (HAS_EXTRA_SPACES.test(out) || /^\s|\s$/.test(out)) {
    out = out.replace(/\s+/g, ' ').trim();
    worked = true;
  } else {
    out = out.trim();
  }

  // Read stopwatch and prepare result
  if (opts.stats) {
    const end = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const seconds = (end - start) / 1000;
    return { text: out, worked, seconds };
  }
  return out;
}

export const stripMarkdownBold = text => {
  if (!text) return text;
  // Replace **text** with either plain text or <strong>text</strong>
  return text.replace(/\*\*(.*?)\*\*/g, '$1'); // For plain text
  // For bold rendering, use: return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

// Alternative: Enhance convertMarkdownToReact to handle bold
export const convertBlogMarkdownToReact = (text, strip = false, maxWords = 125) => {
  if (!text) return '';

  let processedText = text;

  // Handle bold (**text**)
  if (strip) {
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '$1'); // Strip bold markers
  } else {
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // Convert to <strong>
  }

  // Split text into words
  const words = processedText.split(/\s+/).filter(word => word.length > 0);

  // Truncate to maxWords and add ellipsis if necessary
  if (words.length > maxWords) {
    processedText = words.slice(0, maxWords).join(' ') + '...';
  }

  return processedText;
};

export const filterEventsByFilters = (events, selectedFilters) => {
  return (events || []).filter(event => {
    const isGroup = !!event?.attributes?.isGroup;
    if (selectedFilters.has('group') && !isGroup) return false;
    if (selectedFilters.has('individual') && isGroup) return false;
    const tags = event?.attributes?.event_tags?.data || [];
    const hasMenopause = tags.some(t => {
      const a = t?.attributes || {};
      const title = (a.title || '').toLowerCase();
      const slug = (a.slug || '').toLowerCase();
      return title.includes('εμμηνόπαυση') || title.includes('menopause') || slug === 'menopause';
    });
    const hasFamily = tags.some(t => {
      const a = t?.attributes || {};
      const title = (a.title || '').toLowerCase();
      const slug = (a.slug || '').toLowerCase();
      return (
        title.includes('οικογένεια') ||
        title.includes('family') ||
        slug === 'oikogeneia-and-gynaikeia-ygeia' ||
        slug === 'family-program'
      );
    });
    if (selectedFilters.has('menopause') && !hasMenopause) return false;
    if (selectedFilters.has('family') && !hasFamily) return false;
    return true;
  });
};

export const filterByDates = (events, startDate, endDate) => {
  if (!startDate && !endDate) return events || [];
  return (events || []).filter(event => {
    const d = moment(event?.attributes?.date);
    return (
      (!startDate || d.isSameOrAfter(startDate, 'day')) &&
      (!endDate || d.isSameOrBefore(endDate, 'day'))
    );
  });
};

export const computeFilterCounts = allEvents => {
  const counts = { group: 0, individual: 0, menopause: 0, family: 0 };
  (allEvents || []).forEach(e => {
    const isGroup = !!e?.attributes?.isGroup;
    const tags = e?.attributes?.event_tags?.data || [];
    const hasMenopause = tags.some(t => {
      const a = t?.attributes || {};
      const title = (a.title || '').toLowerCase();
      const slug = (a.slug || '').toLowerCase();
      return title.includes('εμμηνόπαυση') || title.includes('menopause') || slug === 'menopause';
    });
    const hasFamily = tags.some(t => {
      const a = t?.attributes || {};
      const title = (a.title || '').toLowerCase();
      const slug = (a.slug || '').toLowerCase();
      return (
        title.includes('οικογένεια') ||
        title.includes('family') ||
        slug === 'oikogeneia-and-gynaikeia-ygeia' ||
        slug === 'family-program'
      );
    });
    if (isGroup) counts.group += 1;
    else counts.individual += 1;
    if (hasMenopause) counts.menopause += 1;
    if (hasFamily) counts.family += 1;
  });
  return counts;
};

export const collectDotDates = (allEvents, selectedFilters) => {
  const pillFiltered = filterEventsByFilters(allEvents, selectedFilters);
  const s = new Set();
  pillFiltered.forEach(e => {
    const d = moment(e?.attributes?.date);
    if (d.isValid()) s.add(d.format('YYYY-MM-DD'));
  });
  return s;
};

/**
 * Finds the first future occurrence (based on end time > now)
 * from a recurring event list.
 *
 * @param {Array} recurringDates - Array of objects like { Date, duration }
 * @param {number} defaultDurationMinutes - fallback duration (in minutes)
 * @returns {moment.Moment|null} first future start moment
 */
export const firstFutureOccurrence = (recurringDates = [], defaultDurationMinutes = 60) => {
  const now = moment();
  const sorted = (recurringDates || [])
    .map(occ => {
      const start = moment(occ?.Date);
      const dur = Number.isFinite(occ?.duration) ? occ.duration : defaultDurationMinutes;
      const end = start.clone().add(dur || 0, 'minutes');
      return { start, end, occ };
    })
    .filter(({ end }) => end.isAfter(now))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());
  return sorted[0]?.start || null;
};

// Last Sunday of a month at a specific hour (monthIndex: 0=Jan … 11=Dec)
const lastSundayAtHour = (year, monthIndex, hour) => {
  const end = moment({ year, month: monthIndex }).endOf('month');
  const lastSunday = end.day() === 0 ? end : end.clone().subtract(end.day(), 'days');
  return lastSunday
    .clone()
    .hour(hour)
    .minute(0)
    .second(0)
    .millisecond(0);
};

// EET/EEST detection (Europe/Athens-like DST rule: last Sun Mar → last Sun Oct)
export const getEetAbbrev = m => {
  const year = m.year();
  const startDST = lastSundayAtHour(year, 2, 3); // last Sun of March @ 03:00
  const endDST = lastSundayAtHour(year, 9, 4); // last Sun of October @ 04:00
  return m.isSameOrAfter(startDST) && m.isBefore(endDST) ? 'EEST' : 'EET';
};

// Return only occurrences whose END is strictly in the future, sorted ascending.
export const getFutureOccurrences = (occurrences, defaultDurationMinutes) => {
  const now = moment();
  return (occurrences || [])
    .map(occ => {
      const start = moment(occ?.Date);
      const dur = Number.isFinite(occ?.duration) ? occ.duration : defaultDurationMinutes;
      const end = start.clone().add(dur || 0, 'minutes');
      return { start, end, dur, raw: occ };
    })
    .filter(({ end }) => end.isAfter(now))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());
};

// helpers (place above filterEventsByFilters)
export const normalize = s =>
  (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, ''); // remove accents (works for Greek too)

// Acceptable slug/title fragments per program key (add more aliases if you add tags)
export const PROGRAM_ALIASES = {
  menopause: [
    'menopause',
    'εμμηνόπαυση', // with accents
    'εμμηνοπαυση', // without accents
  ],
  family: [
    'family',
    'oikogeneia',
    'οικογενεια',
    'γυναικειa υγεια', // normalized match for "Γυναικεία υγεία"
    'γυναικεια υγεια',
  ],
};

export const renderArabicContentModules = () => {
  const currentLocale = getDefaultLocale();

  return currentLocale === 'en';
};

const getWellnessLocaleKey = locale => {
  const normalizedLocale = String(locale || 'en').toLowerCase();
  if (normalizedLocale.startsWith('ar')) return 'ar';
  if (normalizedLocale.startsWith('el')) return 'el';
  if (normalizedLocale.startsWith('de')) return 'de';
  return 'en';
};

export function getScoreDetails(score, locale = getDefaultLocale()) {
  const localeKey = getWellnessLocaleKey(locale);
  const descriptions = {
    en: {
      0: 'Your score indicates significant challenges in your sexual well-being during menopause. Prioritize self-care, explore gentle reconnection practices, and consider consulting a healthcare professional for personalized support.',
      1: 'You’re beginning to explore and nurture your sexual well-being. Focus on consistent rituals that reduce stress, support body confidence, and reignite desire.',
      2: 'Your score reflects a moderate level of sexual well-being with room for enhancement. Build on existing strengths while addressing specific areas of intimacy and desire through targeted practices.',
      3: 'Your score demonstrates a strong foundation in sexual well-being and intimacy. Maintain this positive state by continuing supportive habits and exploring advanced techniques for sustained vitality.',
    },
    el: {
      0: 'Το σκορ σου δείχνει σημαντικές προκλήσεις στη σεξουαλική σου ευεξία κατά την εμμηνόπαυση. Δώσε προτεραιότητα στη φροντίδα του εαυτού σου, δοκίμασε ήπιες πρακτικές επανασύνδεσης και σκέψου να μιλήσεις με έναν επαγγελματία υγείας για εξατομικευμένη υποστήριξη.',
      1: 'Αρχίζεις να εξερευνάς και να φροντίζεις τη σεξουαλική σου ευεξία. Εστίασε σε σταθερές πρακτικές που μειώνουν το στρες, ενισχύουν την αυτοπεποίθηση για το σώμα σου και αναζωπυρώνουν την επιθυμία.',
      2: 'Το σκορ σου δείχνει ένα μέτριο επίπεδο σεξουαλικής ευεξίας με περιθώρια βελτίωσης. Χτίσε πάνω στα δυνατά σου σημεία και δούλεψε πιο στοχευμένα όσα αφορούν την οικειότητα και την επιθυμία.',
      3: 'Το σκορ σου δείχνει μια ισχυρή βάση στη σεξουαλική ευεξία και την οικειότητα. Διατήρησέ τη συνεχίζοντας υποστηρικτικές συνήθειες και εξερευνώντας πιο προχωρημένες πρακτικές για σταθερή ζωντάνια.',
    },
    de: {
      0: 'Ihr Ergebnis weist auf deutliche Herausforderungen für Ihr sexuelles Wohlbefinden in den Wechseljahren hin. Priorisieren Sie Selbstfürsorge, probieren Sie sanfte Übungen zur Wiederannäherung aus und ziehen Sie professionelle Unterstützung für eine persönliche Begleitung in Betracht.',
      1: 'Sie beginnen, Ihr sexuelles Wohlbefinden bewusster wahrzunehmen und zu pflegen. Konzentrieren Sie sich auf beständige Rituale, die Stress reduzieren, das Körpergefühl stärken und die Lust neu beleben.',
      2: 'Ihr Ergebnis zeigt ein mittleres Maß an sexuellem Wohlbefinden mit Potenzial zur Weiterentwicklung. Bauen Sie auf vorhandenen Stärken auf und arbeiten Sie gezielt an Bereichen rund um Intimität und Verlangen.',
      3: 'Ihr Ergebnis zeigt eine starke Grundlage für sexuelles Wohlbefinden und Intimität. Erhalten Sie diesen positiven Zustand, indem Sie unterstützende Gewohnheiten beibehalten und weiterführende Techniken für nachhaltige Vitalität erkunden.',
    },
    ar: {
      0: 'تشير نتيجتك إلى وجود تحديات واضحة في صحتك الجنسية خلال مرحلة انقطاع الطمث. امنحي العناية الذاتية أولوية، وجرّبي ممارسات لطيفة لإعادة التواصل مع جسدك، وفكّري في استشارة مختص صحي للحصول على دعم مخصص.',
      1: 'أنتِ في بداية استكشاف صحتك الجنسية والاهتمام بها. ركّزي على ممارسات منتظمة تقلّل التوتر، وتعزّز الثقة بالجسد، وتساعد على استعادة الرغبة.',
      2: 'تعكس نتيجتك مستوى متوسطًا من الصحة الجنسية مع وجود مجال لمزيد من التحسّن. واصلي البناء على نقاط القوة الموجودة، مع الاهتمام بالجوانب التي تتعلق بالحميمية والرغبة بشكل أكثر تحديدًا.',
      3: 'تُظهر نتيجتك أساسًا قويًا للصحة الجنسية والحميمية. حافظي على هذا التوازن الإيجابي من خلال الاستمرار في العادات الداعمة واستكشاف أساليب أعمق للحفاظ على الحيوية.',
    },
  };

  let range = 0;
  let rangeText = `0-${break1}`;
  let description = descriptions[localeKey]?.[0] || descriptions.en[0];

  if (score > break1 && score <= break2) {
    range = 1;
    rangeText = `${break1 + 1}-${break2}`;
    description = descriptions[localeKey]?.[1] || descriptions.en[1];
  } else if (score > break2 && score <= break3) {
    range = 2;
    rangeText = `${break2 + 1}-${break3}`;
    description = descriptions[localeKey]?.[2] || descriptions.en[2];
  } else if (score > break3) {
    range = 3;
    rangeText = `${break3 + 1}+`;
    description = descriptions[localeKey]?.[3] || descriptions.en[3];
  }

  return { range, rangeText, description };
}

export function getWellnessProgress(currentUser) {
  if (!currentUser) return null;

  return currentUser?.attributes?.profile?.publicData?.wellnessProgress || [];
}

const FALLBACK_IMAGES = [
  articleImg,
  nutritionPlansImage,
  recipeImage,
  psychologyImage,
  fertilityImage,
];

const getRandomFallbackImage = () => {
  const index = Math.floor(Math.random() * FALLBACK_IMAGES.length);
  return FALLBACK_IMAGES[index];
};

export const getTaskThumbnailUrl = currentTask => {
  if (!currentTask) return getRandomFallbackImage();

  // Written guides directly on the task
  const primaryGuides = currentTask?.written_guides?.data;
  // Written guides from relatedResource
  const relatedGuides = currentTask?.relatedResource?.written_guides?.data;

  const guidesArray =
    (Array.isArray(primaryGuides) && primaryGuides.length > 0
      ? primaryGuides
      : Array.isArray(relatedGuides) && relatedGuides.length > 0
      ? relatedGuides
      : null) || null;

  const firstGuide = guidesArray && guidesArray[0];

  const thumbnailUrl = firstGuide?.attributes?.thumbnail?.data?.attributes?.url;

  if (thumbnailUrl) {
    // If Strapi ever returns relative paths, handle here
    // return thumbnailUrl.startsWith("http")
    //   ? thumbnailUrl
    //   : process.env.REACT_APP_STRAPI_URL + thumbnailUrl;

    return thumbnailUrl;
  }

  const { hero } = chooseHeroImage(
    currentTask?.relatedResource?.written_guides?.data[0]?.attributes,
    typeImageMap,
    articleImg
  );
  return hero ?? noImage;
};

// Helper to extract task image URL (no fallback!)
export const getTaskImageUrl = currentTask => {
  if (!currentTask) return null;

  // 1. Direct task image on the task
  const directImage =
    currentTask?.taskImage?.data?.attributes?.formats?.small?.url ||
    currentTask?.taskImage?.data?.attributes?.url;

  if (directImage) return directImage;

  // 2. Task image from relatedResource
  const relatedImage =
    currentTask?.relatedResource?.taskImage?.data?.attributes?.formats?.small?.url ||
    currentTask?.relatedResource?.taskImage?.data?.attributes?.url;

  if (relatedImage) return relatedImage;

  // 3. No task image at all
  return null;
};

export const getRelatedPrograms = prog => {
  if (!prog) return [];
  const currentRp = {
    id: prog.id,
    tasks: prog.tasks || [],
    selfCheck: prog.selfCheck || null,
  };
  const localizedRps = (prog.localizations?.data || []).map(l => ({
    id: l.id,
    tasks: l.attributes?.tasks || [],
    selfCheck: Array.isArray(l.attributes?.selfCheck)
      ? l.attributes.selfCheck[0]
      : l.attributes?.selfCheck || null,
  }));
  return [currentRp, ...localizedRps];
};

export const mapProgressToProgram = (sourceProgress, sourceRp, targetRp) => {
  const targetProgress = { ...sourceProgress, id: targetRp.id };

  // Map tasks: Translate IDs based on task array index
  targetProgress.tasks = sourceProgress.tasks.map(sourcePt => {
    const sourceIndex = sourceRp.tasks.findIndex(t => t.id === sourcePt.id);
    if (sourceIndex === -1) return sourcePt; // Fallback if no match
    const targetTask = targetRp.tasks[sourceIndex];
    return { ...sourcePt, id: targetTask?.id };
  });

  // Self-check can be copied as-is (index-based)
  if (targetProgress.selfCheck && targetRp.selfCheck) {
    targetProgress.selfCheck = { ...targetProgress.selfCheck, id: targetRp.selfCheck.id };
  }

  return targetProgress;
};

export const getEnhancedPrograms = (wellnessPrograms, wellnessProgress, fallbackImages) => {
  return (
    wellnessPrograms?.map((program, index) => {
      let progressEntry = wellnessProgress?.find(entry => entry?.id === program?.id);
      let programEntry = wellnessPrograms?.find(entry => entry?.id === program?.id);

      if (!progressEntry && program?.attributes?.localizations?.data?.length > 0) {
        const locIds = program.attributes.localizations.data.map(l => l.id);
        progressEntry = wellnessProgress?.find(entry => locIds.includes(entry?.id));
      }

      const userTasks = progressEntry?.tasks || [];
      const total = userTasks.length;
      const programTotal = programEntry?.attributes?.tasks?.length;
      const completed = userTasks?.filter(t => !!t?.completed)?.length || 0;
      const progress = total > 0 ? completed / programTotal : 0;
      const hasCustomImage = !!program?.attributes?.programImage?.data;
      const defaultImage = fallbackImages[index % fallbackImages.length];

      return {
        id: program?.id,
        image: hasCustomImage
          ? program?.attributes?.programImage?.data?.attributes?.url
          : defaultImage,
        title: program?.attributes?.title,
        description: program?.attributes?.description,
        tasks: program?.attributes?.tasks,
        status: progressEntry ? progressEntry?.status : null,
        progress,
      };
    }) || []
  );
};

export const getUserType = currentUser => {
  if (!currentUser) {
    return null;
  }
  return currentUser?.attributes?.profile?.protectedData?.userType || null;
};

export const getUserId = currentUser => {
  if (!currentUser) {
    return null;
  }
  return currentUser?.id?.uuid || null;
};

export function getVimeoVideoId(url) {
  const regex = /(?:vimeo\.com\/)(\d+)/;
  const match = url.match(regex);
  if (match && match[1]) {
    return match[1];
  }
  return null;
}

export function getCurrentPage() {
  const currentPath = typeof window !== 'undefined' && window.location.pathname;
  return currentPath.replace(/^\/+|\/+$/g, '') || 'home';
}

export const getRangeType = period => {
  switch (period) {
    case 'thisWeek':
      return '1 week';
    case 'twoWeeks':
      return '2 weeks';
    case 'thisMonth':
      return 'this month';
    case 'custom':
      return 'custom';
    default:
      return period;
  }
};

export const formatDateYYYYMMDD = date => moment(date).format('YYYY-MM-DD');

export const getSubscriptionEndDate = currentSubscription => {
  if (!currentSubscription) {
    return null;
  }

  const isTrialing = currentSubscription.status === 'trialing';

  if (isTrialing && currentSubscription.trial_end) {
    return new Date(currentSubscription.trial_end * 1000);
  }

  // If the subscription has already ended, return the ended_at date
  if (currentSubscription.ended_at) {
    return new Date(currentSubscription.ended_at * 1000);
  }

  // If a specific cancel_at timestamp is set, return that date
  if (currentSubscription.cancel_at) {
    return new Date(currentSubscription.cancel_at * 1000);
  }

  // If set to cancel at the end of the current period, return current_period_end
  if (currentSubscription.current_period_end) {
    return new Date(currentSubscription.current_period_end * 1000);
  }

  if (currentSubscription.trial_end) {
    return new Date(currentSubscription.trial_end * 1000);
  }

  // If the subscription is active and not scheduled to cancel, it has no end date
  return null;
};

export const validateSpecialCoupon = (coupon, currentUser) => {
  // Special validation for NNVIV26 coupon (domain-specific)
  if (coupon === 'NNVIV26') {
    const email = currentUser?.attributes?.email?.toLowerCase().trim();
    const appliedCoupons = currentUser?.attributes?.profile?.privateData?.appliedCoupons || [];

    if (!email) {
      return { valid: false, error: 'User email not found.' };
    }

    const allowedDomains = ['@nnhellas.gr', '@nnadvisors.gr', '@yopmail.com'];
    const hasValidDomain = allowedDomains.some(domain => email.endsWith(domain));

    if (!hasValidDomain) {
      return {
        valid: false,
        error: 'This coupon is not valid for this domain email.',
      };
    }

    const isCouponApplied = appliedCoupons.includes(coupon);

    if (isCouponApplied) {
      return { valid: false, error: 'This coupon can only be used once per user.' };
    }

    return { valid: true, error: null };
  }

  // Universal validation: Check if user has already used this coupon
  // This applies to ALL coupons, ensuring one-time use per user
  if (currentUser) {
    const appliedCoupons = currentUser?.attributes?.profile?.privateData?.appliedCoupons || [];
    const isCouponApplied = appliedCoupons.includes(coupon);

    if (isCouponApplied) {
      return { valid: false, error: 'This coupon can only be used once per user.' };
    }
  }

  return { valid: true, error: null };
};

export const formatUTCDate = date =>
  moment
    .utc(date)
    .add(8, 'hours')
    .format('YYYY-MM-DDTHH:mm:ss.000Z');

export const getCurrencySymbol = currency => {
  if (currency === 'USD') return '$';
  if (currency === 'EUR') return '€';
  return currency;
};

export const getContactDetailsInitialValues = user => {
  if (!user || !user?.id) return null;
  const protectedData = user?.attributes?.profile?.protectedData || {};
  const profile = user?.attributes?.profile || {};

  const email = user?.attributes?.email || '';
  const phoneNumber = protectedData?.phoneNumber || '';

  const initialValues = {
    email,
    phoneNumber,
    dob: protectedData?.dob,
    salutation: protectedData?.salutation,
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    country: protectedData?.country,
  };

  return initialValues;
};

export const getInitialConsentValues = currentUser => {
  if (!currentUser || !currentUser?.id) return null;
  const protectedData = currentUser?.attributes?.profile?.protectedData || {};

  return {
    newsletterConsent: protectedData?.newsLetterConsent ?? false,
    aiUsageConsent: protectedData?.aiUsageConsent ?? false,
    symptomTrackingConsent: protectedData?.symptomTrackingConsent ?? false,
  };
};

export const getUserProtectedData = currentUser => {
  if (!currentUser || !currentUser?.id) return null;
  const protectedData = currentUser?.attributes?.profile?.protectedData || {};

  return protectedData;
};

/**
 * Get the user's IP address using a public IP detection service
 * Falls back to 'unknown' if the request fails
 * @returns {Promise<string>} The IP address or 'unknown'
 */
export const getIpAddress = async () => {
  try {
    // Using ipify.org - a free, reliable IP detection service
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch IP address');
    }

    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    console.warn('Could not fetch IP address:', error);
    // Return 'unknown' as fallback to allow the app to continue functioning
    return 'unknown';
  }
};

export const openExternalLink = url => {
  if (typeof window === 'undefined') return;

  window.open(url, '_blank', 'noopener,noreferrer');
};

export const getIsCurrentUserTesting = (testEmail, user) => {
  if (!testEmail || !user) return false;

  const userEmail = user?.attributes?.email;
  return testEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();
};
